import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Spinner, Surface, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { RosterUser } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { SELLER_LOGIN_SCOPE } from '@/components/shortcuts';
import { PinPad } from '@/components/pinPad/PinPad';
import { usePinPhysicalKeys } from '@/components/pinPad/usePinPhysicalKeys';
import type { PinErrorMessage } from '@/components/pinPad/usePinAttemptError';
import { getInitials } from '../sellerLoginMeta';
import { usePinStepFocus } from './usePinStepFocus';

type SellerPinStepProps = {
    user: RosterUser;
    pin: string;
    pinErrorMessage: PinErrorMessage | null;
    /** The server asked us to wait: the pad takes no input until it ends. */
    isPinHeld: boolean;
    isSubmitting: boolean;
    onDigit: (digit: string) => void;
    onBackspace: () => void;
    onEscape: () => void;
    onBack: () => void;
};

const PIN_NAME_ID = 'seller-login-pin-name';
const PIN_PROMPT_ID = 'seller-login-pin-prompt';

/**
 * Step 2 (render-only): the chosen seller in an identity card, then the shared
 * kiosk `PinPad`. The hook auto-submits on the fourth digit. A lockout, a rate
 * limit (with its countdown, the pad held meanwhile) and a wrong PIN each have
 * their own localized message.
 *
 * Feeds the pad from the physical keyboard through `usePinPhysicalKeys` — the
 * same `onDigit`/`onBackspace` path the on-screen pad uses, so keyboard parity
 * costs the pad nothing — while the screen's `sellerLogin` scope is live
 * (`SellerLogin` pushes it). The step's card is a group named for the seller
 * and the prompt, and takes focus when it appears (`usePinStepFocus`): the
 * roster tile that had it is gone.
 */
export const SellerPinStep: React.FC<SellerPinStepProps> = ({
    user,
    pin,
    pinErrorMessage,
    isPinHeld,
    isSubmitting,
    onDigit,
    onBackspace,
    onEscape,
    onBack,
}) => {
    const { t } = useTranslation('pages');

    const flashedKey = usePinPhysicalKeys({
        isDisabled: isSubmitting || isPinHeld,
        onBackspace,
        onDigit,
        onEscape,
        scope: SELLER_LOGIN_SCOPE,
    });
    const stepRef = usePinStepFocus<HTMLDivElement>();

    const errorMessage = pinErrorMessage
        ? t(pinErrorMessage.key, pinErrorMessage.values)
        : null;

    return (
        <div
            className="w-full max-w-xs"
            {...getNodeTestIdProp('seller-login-pin')}
        >
            <Surface
                aria-labelledby={`${PIN_NAME_ID} ${PIN_PROMPT_ID}`}
                className="flex flex-col items-center gap-6 rounded-3xl p-6"
                ref={stepRef}
                role="group"
                tabIndex={-1}
                variant="default"
            >
                <Surface
                    className="flex w-full flex-col items-center gap-3 rounded-2xl p-5 text-center"
                    variant="secondary"
                >
                    <Avatar size="lg">
                        <Avatar.Fallback>
                            {getInitials(user.name)}
                        </Avatar.Fallback>
                    </Avatar>
                    <div className="flex flex-col gap-1">
                        <Typography id={PIN_NAME_ID} type="h4" weight="bold">
                            {user.name}
                        </Typography>
                        <Typography
                            color="muted"
                            id={PIN_PROMPT_ID}
                            type="body-sm"
                        >
                            {t('sellerLogin.pinPrompt')}
                        </Typography>
                    </div>
                </Surface>

                <div className="flex min-h-5 items-center justify-center text-center">
                    {errorMessage ? (
                        <Typography
                            className="text-danger"
                            {...getNodeTestIdProp('seller-login-pin-error')}
                            type="body-sm"
                        >
                            {errorMessage}
                        </Typography>
                    ) : isSubmitting ? (
                        <Spinner
                            aria-label={t('sellerLogin.signingIn')}
                            size="sm"
                        />
                    ) : null}
                </div>

                <PinPad
                    flashedKey={flashedKey}
                    hasError={!!errorMessage}
                    isDisabled={isSubmitting || isPinHeld}
                    onBackspace={onBackspace}
                    onDigit={onDigit}
                    pin={pin}
                />

                <Button
                    {...getNodeTestIdProp('seller-login-pin-back')}
                    isDisabled={isSubmitting}
                    onPress={onBack}
                    size="sm"
                    variant="ghost"
                >
                    <Icon className="size-4" icon="solar:arrow-left-linear" />
                    {t('sellerLogin.back')}
                </Button>
            </Surface>
        </div>
    );
};
